import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Filter,
  Repeat,
  Shuffle,
  Sparkles,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  X,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { cn } from "../../../../utils/function";
import {
  useExecutionsSummary,
  useExecutionsByWorkflow,
  useExecutionsByChannel,
  useExecutionsList,
  useExecutionDetail,
  ExecutionsSummary,
  WorkflowItem,
  ChannelBar,
  ExecutionItem,
  ExecutionDetail,
} from "./executions-api";

// Rebuilt against the real, now-live Executions API (journeybuilder.connect
// .dalmlabs.com) per "Bot Builder & Executions API — Frontend Reference" —
// nothing here is hardcoded any more; every card, table, tab and the detail
// drawer reads straight from GET /v1/executions/summary, /by-workflow,
// /by-channel, /v1/executions and /v1/executions/{session_id}.

type ExecState = "running" | "waiting" | "failed" | "completed";
const STATE_LABEL: Record<ExecState, string> = { running: "Running", waiting: "Waiting", failed: "Failed", completed: "Completed" };
const TABS: ExecState[] = ["running", "waiting", "failed", "completed"];

const CHANNEL_BADGE: Record<string, { label: string; className: string; icon?: React.ReactNode }> = {
  sms: { label: "SMS", className: "border-amber-300 text-amber-700 dark:border-amber-500/40 dark:text-amber-400", icon: <MessageCircle size={12} className="fill-current" /> },
  whatsapp: { label: "WhatsApp", className: "border-emerald-300 text-emerald-700 dark:border-emerald-500/40 dark:text-emerald-400", icon: <FaWhatsapp size={12} /> },
};
const genericChannelBadge = (ch: string) => ({
  label: ch === "unspecified" ? "Unspecified" : ch.replace(/\b\w/g, (c) => c.toUpperCase()),
  className: ch === "unspecified" ? "border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400" : "border-indigo-300 text-indigo-700 dark:border-indigo-500/40 dark:text-indigo-400",
});

const CHANNEL_BAR_COLORS = ["bg-indigo-500", "bg-lime-500", "bg-teal-500", "bg-rose-400", "bg-sky-500", "bg-amber-500"];

const STATE_BADGE: Record<string, string> = {
  running: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
  waiting: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/30",
  failed: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
};

const TIMELINE_STATE_COLOR: Record<string, string> = {
  done: "text-emerald-600 dark:text-emerald-400",
  current: "text-sky-600 dark:text-sky-400",
  pending: "text-gray-400 dark:text-gray-500",
  skipped: "text-gray-300 dark:text-gray-600 line-through",
};
const TIMELINE_DOT_COLOR: Record<string, string> = {
  done: "bg-emerald-500",
  current: "bg-sky-500",
  pending: "bg-gray-300 dark:bg-gray-600",
  skipped: "bg-gray-200 dark:bg-gray-700",
};

function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || isNaN(totalSeconds)) return "—";
  const s = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "—";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 0) return "just now";
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

// Small debounce so real search/filter changes don't fire a network request
// on every keystroke.
function useDebounced<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ─── Small reusable bits ────────────────────────────────────────────────────
const Dropdown: React.FC<{ value: string; options: { label: string; value: string }[]; onChange: (v: string) => void; className?: string }> = ({
  value,
  options,
  onChange,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const activeLabel = options.find((o) => o.value === value)?.label ?? value;
  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <span className="truncate">{activeLabel}</span>
        <ChevronDown size={14} className={cn("text-gray-400 dark:text-gray-500 transition-transform shrink-0", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden py-1 max-h-64 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={cn(
                "block w-full text-left px-3.5 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 truncate",
                opt.value === value ? "text-indigo-600 dark:text-indigo-400 font-medium" : "text-gray-700 dark:text-gray-200"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StateBadge: React.FC<{ state: string }> = ({ state }) => (
  <span className={cn("inline-flex px-2.5 py-1 rounded-md border text-xs font-medium capitalize", STATE_BADGE[state] || "border-gray-200 text-gray-600")}>{state}</span>
);

// ─── Execution detail drawer ────────────────────────────────────────────────
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4">
      <button type="button" onClick={() => setOpen((s) => !s)} className="w-full flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
        {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {open && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode; code?: boolean }> = ({ label, value, code }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-gray-400 dark:text-gray-500 shrink-0">{label}</span>
    {code ? (
      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-mono truncate">{value}</span>
    ) : (
      <span className="text-gray-900 dark:text-gray-100 text-right truncate">{value}</span>
    )}
  </div>
);

const humanizeKey = (k: string) => k.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ExecutionDrawer: React.FC<{ sessionId: string; onClose: () => void }> = ({ sessionId, onClose }) => {
  const [detail, setDetail] = useState<ExecutionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const { get: getDetail } = useExecutionDetail();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const d = await getDetail(sessionId);
      if (cancelled) return;
      setLoading(false);
      if (d) setDetail(d);
      else toast.error("Couldn't load this execution's detail.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const submittedEntries = detail?.submitted_data ? Object.entries(detail.submitted_data) : [];
  const variableEntries = detail?.variables ? Object.entries(detail.variables) : [];

  return (
    <div className="w-full lg:w-[400px] shrink-0 border-l border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 overflow-y-auto">
      {loading || !detail ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={20} className="animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 pb-4 border-b border-gray-100 dark:border-gray-800">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide truncate">{detail.journey_name}</h3>
                <StateBadge state={detail.status} />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Journey ID: {detail.journey_id}</p>
            </div>
            <button onClick={onClose} className="p-1 -mt-1 -mr-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0">
              <X size={16} />
            </button>
          </div>

          <Section title={`Execution ${detail.session_id}`}>
            <DetailRow label="Trigger:" value={detail.trigger_event} code />
            <DetailRow label="Contact ID:" value={detail.contact_id} code />
            <DetailRow label="Current step:" value={detail.current_step} />
            {detail.next_step && <DetailRow label="Next step:" value={detail.next_step} />}
            <DetailRow label="Started at:" value={formatDateTime(detail.started_at)} />
            <DetailRow label="Duration:" value={formatDuration(detail.duration_seconds)} />
          </Section>

          {submittedEntries.length > 0 && (
            <Section title="Submitted data">
              {submittedEntries.map(([k, v]) => (
                <DetailRow key={k} label={`${humanizeKey(k)}:`} value={String(v)} />
              ))}
            </Section>
          )}

          {variableEntries.length > 0 && (
            <Section title="Variables">
              {variableEntries.map(([k, v]) => (
                <DetailRow key={k} label={`${humanizeKey(k)}:`} value={String(v)} code />
              ))}
            </Section>
          )}

          <div className="py-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Execution Timeline</h4>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {detail.timeline.completed} of {detail.timeline.total_steps} · {detail.timeline.percent_complete}%
              </span>
            </div>
            <div className="space-y-0">
              {detail.timeline.steps.map((step, i) => (
                <div key={step.id || i} style={{ marginLeft: step.depth * 16 }}>
                  <div className="flex items-start gap-2.5">
                    <span className={cn("w-1.5 h-1.5 rounded-full mt-1.5 shrink-0", TIMELINE_DOT_COLOR[step.state] || "bg-gray-300")} />
                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={cn("text-sm", step.state === "skipped" ? "text-gray-400 dark:text-gray-500" : "text-gray-800 dark:text-gray-100")}>
                          {step.branch && <span className="text-[10px] font-semibold text-indigo-500 mr-1">[{step.branch}]</span>}
                          {step.label}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{step.completed_at ? formatDateTime(step.completed_at) : "Not reached yet"}</p>
                      </div>
                      <span className={cn("text-xs font-medium shrink-0 capitalize", TIMELINE_STATE_COLOR[step.state] || "text-gray-500")}>{step.state}</span>
                    </div>
                  </div>
                  {step.since_previous_seconds != null && (
                    <div className="ml-[5px] pl-4 py-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[11px] font-medium">
                        <Clock size={10} /> {formatDuration(step.since_previous_seconds)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Shared execution list — self-contained, fetches its own page ─────────
const PER_PAGE = 25;

const ExecutionList: React.FC<{
  journeyId?: number;
  workflowOptions: { journey_id: number; name: string }[];
  searchPlaceholder: string;
  onSelect: (sessionId: string) => void;
  selectedId?: string;
  initialTab?: ExecState;
  initialStalled?: boolean;
}> = ({ journeyId, workflowOptions, searchPlaceholder, onSelect, selectedId, initialTab = "running", initialStalled = false }) => {
  const [tab, setTab] = useState<ExecState>(initialTab);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [journeyFilter, setJourneyFilter] = useState<string>(journeyId != null ? String(journeyId) : "all");
  const [stalledOnly, setStalledOnly] = useState(initialStalled);
  const [page, setPage] = useState(1);

  const [result, setResult] = useState<{ page: number; per_page: number; total: number; tabs: Record<ExecState, number>; items: ExecutionItem[] } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const { loading, get, ready } = useExecutionsList();

  const effectiveJourneyId = journeyId != null ? journeyId : journeyFilter !== "all" ? Number(journeyFilter) : undefined;

  const fetchList = useCallback(async () => {
    if (!ready) return;
    setLoadError(false);
    const res = await get({ search: debouncedSearch || undefined, journey_id: effectiveJourneyId, status: tab, stalled: stalledOnly || undefined, page, per_page: PER_PAGE });
    if (res) setResult(res);
    else {
      setLoadError(true);
      toast.error("Couldn't load executions.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, debouncedSearch, effectiveJourneyId, tab, stalledOnly, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, effectiveJourneyId, tab, stalledOnly]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const workflowNameById = useMemo(() => Object.fromEntries(workflowOptions.map((w) => [w.journey_id, w.name])), [workflowOptions]);
  const showWorkflowColumn = journeyId == null;
  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.per_page)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl">
          <Search size={15} className="text-gray-400 dark:text-gray-500 shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent outline-none text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
        </div>
        {journeyId == null && (
          <Dropdown
            value={journeyFilter}
            options={[{ label: "All workflows", value: "all" }, ...workflowOptions.map((w) => ({ label: w.name, value: String(w.journey_id) }))]}
            onChange={setJourneyFilter}
            className="sm:w-[220px]"
          />
        )}
        <button
          type="button"
          onClick={() => setStalledOnly((s) => !s)}
          className={cn(
            "flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium rounded-xl border whitespace-nowrap transition-colors",
            stalledOnly
              ? "bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-400"
              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          )}
        >
          <AlertTriangle size={14} /> Stalled only
        </button>
      </div>

      <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors",
              tab === t ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400"
            )}
          >
            {STATE_LABEL[t]} <span className="text-gray-400 dark:text-gray-500 font-medium">{result?.tabs?.[t] ?? "—"}</span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="text-left text-sm font-medium text-gray-500 dark:text-gray-400">
              <th className="font-medium pb-3">Execution ID</th>
              <th className="font-medium pb-3">Customer/business identifier</th>
              <th className="font-medium pb-3">State</th>
              {showWorkflowColumn && <th className="font-medium pb-3">Workflow</th>}
              <th className="font-medium pb-3">Current step</th>
              <th className="font-medium pb-3">Started at</th>
              <th className="font-medium pb-3">Duration</th>
            </tr>
          </thead>
          <tbody>
            {loading && !result ? (
              <tr>
                <td colSpan={showWorkflowColumn ? 7 : 6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                  <Loader2 size={16} className="inline animate-spin mr-1.5" /> Loading executions…
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td colSpan={showWorkflowColumn ? 7 : 6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                  Couldn't load executions.{" "}
                  <button onClick={fetchList} className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                    Retry
                  </button>
                </td>
              </tr>
            ) : !result || result.items.length === 0 ? (
              <tr>
                <td colSpan={showWorkflowColumn ? 7 : 6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                  No {STATE_LABEL[tab].toLowerCase()} executions to show.
                </td>
              </tr>
            ) : (
              result.items.map((e) => (
                <tr
                  key={e.session_id}
                  onClick={() => onSelect(e.session_id)}
                  className={cn(
                    "border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60",
                    selectedId === e.session_id && "bg-indigo-50/60 dark:bg-indigo-500/10"
                  )}
                >
                  <td className="py-3.5 pr-4 font-mono text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{e.session_id}</td>
                  <td className="py-3.5 pr-4 text-gray-700 dark:text-gray-200 whitespace-nowrap">{e.contact_id}</td>
                  <td className="py-3.5 pr-4">
                    <StateBadge state={e.status} />
                  </td>
                  {showWorkflowColumn && (
                    <td className="py-3.5 pr-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">{workflowNameById[e.journey_id] || e.trigger_event}</td>
                  )}
                  <td className="py-3.5 pr-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {e.current_step}
                    {e.next_step && <span className="text-gray-400 dark:text-gray-500"> → {e.next_step}</span>}
                  </td>
                  <td className="py-3.5 pr-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDateTime(e.started_at)}</td>
                  <td className="py-3.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDuration(e.duration_seconds)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {result && result.total > result.per_page && (
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Page {result.page} of {totalPages} · {result.total} total
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Top-level page ─────────────────────────────────────────────────────────
type View = "overview" | "all" | "workflow";

const ExecutionsPage: React.FC = () => {
  const [view, setView] = useState<View>("overview");
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");
  const [overviewSearch, setOverviewSearch] = useState("");
  const debouncedOverviewSearch = useDebounced(overviewSearch);
  const [overviewStatusFilter, setOverviewStatusFilter] = useState("all");
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowItem | null>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [initialTab, setInitialTab] = useState<ExecState>("running");
  const [initialStalled, setInitialStalled] = useState(false);

  const [summary, setSummary] = useState<ExecutionsSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const { get: getSummary, loading: summaryLoading, ready: summaryReady } = useExecutionsSummary();

  const [workflowsResult, setWorkflowsResult] = useState<{ total: number; items: WorkflowItem[] } | null>(null);
  const { get: getByWorkflow, loading: workflowsLoading, ready: workflowsReady } = useExecutionsByWorkflow();

  const [channelsResult, setChannelsResult] = useState<{ total: number; channels: ChannelBar[] } | null>(null);
  const { get: getByChannel, ready: channelsReady } = useExecutionsByChannel();

  // All workflows seen across both Active/In-active fetches — used to build
  // the journey_id -> name lookup for the "all executions" table's Workflow
  // column and its "All workflows" filter.
  const [knownWorkflows, setKnownWorkflows] = useState<Record<number, string>>({});

  const fetchSummary = useCallback(async () => {
    if (!summaryReady) return;
    setSummaryError(false);
    const s = await getSummary(days);
    if (s) setSummary(s);
    else setSummaryError(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryReady, days]);

  const fetchWorkflows = useCallback(async () => {
    if (!workflowsReady) return;
    const res = await getByWorkflow({ active: activeTab === "active", search: debouncedOverviewSearch || undefined });
    if (res) {
      setWorkflowsResult(res);
      setKnownWorkflows((prev) => ({ ...prev, ...Object.fromEntries(res.items.map((w) => [w.journey_id, w.name])) }));
    } else toast.error("Couldn't load the workflow table.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowsReady, activeTab, debouncedOverviewSearch]);

  const fetchChannels = useCallback(async () => {
    if (!channelsReady) return;
    const res = await getByChannel();
    if (res) setChannelsResult(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelsReady]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Also warm the name lookup with the *other* active-state bucket once, so
  // the "all executions" view's Workflow column resolves inactive journeys'
  // names too, not just whichever tab happens to be selected right now.
  useEffect(() => {
    if (!workflowsReady) return;
    (async () => {
      const res = await getByWorkflow({ active: activeTab !== "active" });
      if (res) setKnownWorkflows((prev) => ({ ...prev, ...Object.fromEntries(res.items.map((w) => [w.journey_id, w.name])) }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowsReady]);

  const visibleWorkflows = useMemo(() => {
    let rows = workflowsResult?.items ?? [];
    if (overviewStatusFilter === "success-heavy") rows = rows.filter((w) => (w.success_rate ?? 0) >= 98);
    if (overviewStatusFilter === "has-failures") rows = rows.filter((w) => w.failed_count > 0);
    return rows;
  }, [workflowsResult, overviewStatusFilter]);

  const workflowOptions = useMemo(() => Object.entries(knownWorkflows).map(([id, name]) => ({ journey_id: Number(id), name })), [knownWorkflows]);

  const openWorkflowRow = (w: WorkflowItem) => {
    setSelectedWorkflow(w);
    setSelectedExecutionId(null);
    setInitialTab("running");
    setView("workflow");
  };

  const openAllExecutions = (tab: ExecState, stalled = false) => {
    setSelectedWorkflow(null);
    setSelectedExecutionId(null);
    setInitialTab(tab);
    setInitialStalled(stalled);
    setView("all");
  };

  const closeDrillDown = () => {
    setView("overview");
    setSelectedWorkflow(null);
    setSelectedExecutionId(null);
  };

  if (summaryLoading && !summary && !summaryError) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Loader2 size={22} className="animate-spin" />
          <p className="text-sm">Loading Executions…</p>
        </div>
      </div>
    );
  }

  if (summaryError && !summary) {
    return (
      <div className="min-h-screen bg-[#FAFAFC] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400 text-center px-6">
          <p className="text-sm font-medium">Couldn't load the Executions dashboard.</p>
          <button type="button" onClick={fetchSummary} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">
            <RefreshCcw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const STAT_CARDS_ROW1: { label: string; icon: React.ComponentType<any>; value: string; onClick?: () => void }[] | null = summary && [
    { label: "Active workflows", icon: Filter, value: String(summary.workflows.active) },
    { label: "Total executions/sessions", icon: Repeat, value: String(summary.executions.total), onClick: () => openAllExecutions("running") },
    { label: "Success rate", icon: Shuffle, value: summary.success_rate == null ? "—" : `${summary.success_rate}%`, onClick: () => openAllExecutions("completed") },
    { label: "Failed executions", icon: Sparkles, value: String(summary.executions.failed), onClick: () => openAllExecutions("failed") },
  ];
  const STAT_CARDS_ROW2 = summary && [
    { label: "Customers reached", icon: Filter, value: String(summary.customers.reached) },
    { label: "Unique customers", icon: Shuffle, value: String(summary.customers.unique) },
    { label: "Repeat customers", icon: Repeat, value: String(summary.customers.repeat) },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFC] dark:bg-gray-950 p-4 sm:p-6">
      <div className="flex gap-0">
        <div className="flex-1 min-w-0 space-y-5">
          {view === "overview" && summary ? (
            <>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Executions</h1>
                <div className="mt-3">
                  <Dropdown
                    value={String(days)}
                    options={[
                      { label: "Last 7 days", value: "7" },
                      { label: "Last 30 days", value: "30" },
                      { label: "Last 90 days", value: "90" },
                    ]}
                    onChange={(v) => setDays(Number(v))}
                    className="w-[160px]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {STAT_CARDS_ROW1!.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.label}
                      type="button"
                      disabled={!c.onClick}
                      onClick={c.onClick}
                      className={cn(
                        "text-left bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 transition-colors w-full",
                        c.onClick ? "hover:border-gray-200 dark:hover:border-gray-700 cursor-pointer" : "cursor-default"
                      )}
                    >
                      <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-2">
                        <Icon size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        <span className="truncate">{c.label}</span>
                      </div>
                      <p className={cn("text-2xl font-semibold", c.onClick ? "text-indigo-600 dark:text-indigo-400 underline decoration-1 underline-offset-2" : "text-gray-900 dark:text-gray-100")}>
                        {c.value}
                      </p>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {STAT_CARDS_ROW2!.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
                      <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-2">
                        <Icon size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        <span className="truncate">{c.label}</span>
                      </div>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{c.value}</p>
                    </div>
                  );
                })}
              </div>

              {summary.executions.stalled > 0 && (
                <button
                  type="button"
                  onClick={() => openAllExecutions("running", true)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-left hover:bg-amber-100 dark:hover:bg-amber-500/15"
                >
                  <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>{summary.executions.stalled}</strong> execution{summary.executions.stalled === 1 ? "" : "s"} haven't moved in {summary.executions.stalled_after_days}+ days — view them
                  </span>
                </button>
              )}

              <div className="flex flex-col sm:flex-row gap-2.5">
                <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl">
                  <Search size={15} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  <input
                    value={overviewSearch}
                    onChange={(e) => setOverviewSearch(e.target.value)}
                    placeholder="Search Workflow"
                    className="w-full bg-transparent outline-none text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>
                <Dropdown
                  value={overviewStatusFilter}
                  options={[
                    { label: "All status", value: "all" },
                    { label: "Success-heavy", value: "success-heavy" },
                    { label: "Has failures", value: "has-failures" },
                  ]}
                  onChange={setOverviewStatusFilter}
                  className="sm:w-[170px]"
                />
              </div>

              <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab("active")}
                  className={cn(
                    "flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors",
                    activeTab === "active" ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400"
                  )}
                >
                  Active <span className="text-gray-400 dark:text-gray-500 font-medium">{summary.workflows.active}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("inactive")}
                  className={cn(
                    "flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors",
                    activeTab === "inactive" ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400"
                  )}
                >
                  In-active <span className="text-gray-400 dark:text-gray-500 font-medium">{summary.workflows.inactive}</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm">
                  <thead>
                    <tr className="text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      <th className="font-medium pb-3">Workflow</th>
                      <th className="font-medium pb-3">Channels</th>
                      <th className="font-medium pb-3">Runs / Sessions</th>
                      <th className="font-medium pb-3">Customers</th>
                      <th className="font-medium pb-3">Success</th>
                      <th className="font-medium pb-3">Failed</th>
                      <th className="font-medium pb-3">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workflowsLoading && !workflowsResult ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                          <Loader2 size={16} className="inline animate-spin mr-1.5" /> Loading workflows…
                        </td>
                      </tr>
                    ) : visibleWorkflows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                          No {activeTab} workflows match these filters.
                        </td>
                      </tr>
                    ) : (
                      visibleWorkflows.map((row) => (
                        <tr
                          key={row.journey_id}
                          onClick={() => openWorkflowRow(row)}
                          className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                        >
                          <td className="py-3.5 pr-4 text-gray-800 dark:text-gray-100 font-medium whitespace-nowrap">{row.name}</td>
                          <td className="py-3.5 pr-4">
                            <div className="flex items-center gap-1.5">
                              {row.channels.map((ch) => {
                                const badge = CHANNEL_BADGE[ch] || genericChannelBadge(ch);
                                return (
                                  <span key={ch} className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium", badge.className)}>
                                    {badge.icon} {badge.label}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="py-3.5 pr-4 text-gray-700 dark:text-gray-200">{row.runs.toLocaleString()}</td>
                          <td className="py-3.5 pr-4 text-gray-700 dark:text-gray-200">{row.customers.toLocaleString()}</td>
                          <td className="py-3.5 pr-4 text-gray-700 dark:text-gray-200">
                            {row.success_rate == null ? "—" : `${row.success_rate}%`} <span className="text-gray-400 dark:text-gray-500">{row.success_count}</span>
                          </td>
                          <td className="py-3.5 pr-4 text-gray-700 dark:text-gray-200">{row.failed_count}</td>
                          <td className="py-3.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">{timeAgo(row.last_activity)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Channel breakdown</h2>
                {!channelsResult ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
                ) : (
                  <div className="space-y-5">
                    {channelsResult.channels.map((c, i) => (
                      <div key={c.channel}>
                        <p className="text-sm text-gray-700 dark:text-gray-200 mb-1.5 capitalize">{c.channel}</p>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                            <div className={cn("h-full rounded-full", CHANNEL_BAR_COLORS[i % CHANNEL_BAR_COLORS.length])} style={{ width: `${Math.max(2, c.percent)}%` }} />
                          </div>
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-12 text-right shrink-0">{c.percent.toFixed(1)}%</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-14 text-right shrink-0">{c.executions.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
                <button type="button" onClick={closeDrillDown} className="hover:underline">
                  Executions
                </button>
                {view === "workflow" && selectedWorkflow && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600 mx-1.5">/</span>
                    <span>{selectedWorkflow.name}</span>
                  </>
                )}
                {view === "all" && <span className="text-gray-300 dark:text-gray-600 mx-1.5 text-lg font-normal"> — Total Executions</span>}
              </h1>

              <ExecutionList
                journeyId={view === "workflow" ? selectedWorkflow?.journey_id : undefined}
                workflowOptions={workflowOptions}
                searchPlaceholder={view === "all" ? "Search Workflow" : "Search by execution ID or customer..."}
                onSelect={(id) => setSelectedExecutionId(id)}
                selectedId={selectedExecutionId || undefined}
                initialTab={initialTab}
                initialStalled={initialStalled}
              />
            </>
          )}
        </div>

        {selectedExecutionId && <ExecutionDrawer sessionId={selectedExecutionId} onClose={() => setSelectedExecutionId(null)} />}
      </div>
    </div>
  );
};

export default ExecutionsPage;
